import AppLayout from "@/layout/AppLayout";
import DashboardHeader from "./DashboardHeader";
import { Typography } from "../ui/typography";
import LandingPageLightIcon from "@/assets/svg/alleralandingpageLogo.svg";
import landingPageDarkIcon from "@/assets/svg/alleraLandingPageDarkLogo.svg";
import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Patient } from "@/store/slices/searchSlice";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedPatient } from "@/store/slices/searchSlice";
import type { RootState, AppDispatch } from "@/store/store";
import { fetchUserDashboardCards } from "@/store/slices/dashboardSlice";
import { routes } from "@/utils/urls";
import DemographicsCard from "./DemographicsCard";
import PCPCard from "./PCPCard";
import HealthPlanCard from "./HealthPlanCard";
import CareManagementCard from "./CareManagementCard";
import AdtAlertsCard from "./AdtAlertsCard/AdtAlertsCard";
import BehavioralHealthCard from "./BehavioralHealthDiagnosesCard";
import SDOHBarriersCard from "./SDOHBarriersCard";
import ReferralsCard from "./Referrals/Referrals";
import MedicalDiagnosesCard from "./MedicalDiagnosesCard";
import CostUtilizationCard from "./CostUtilizationCard";
import MedicationFillHistoryCard from "./MedicationFillHistory";
import ImmunizationsCard from "./Immunizations/ImmunizationsCard";
import CareGaps from "./CareGaps";
import RiskStratificationCard from "./RiskStratificationCard";
import RecentVisitsCard from "./RecentVisitsCard";
import BannerAlert from "../common/BannerAlert";
import type { DashboardCard, ApiCardType } from "@/store/slices/dashboardSlice";
import { ALL_CARD_TYPES } from "@/constants/utilityFunctions";
import api from "@/axios";
import PreferredLayout from "../EditLayout/PreferredLayout";

// Banner notification type from API
interface BannerNotification {
  id: string;
  displayText: string;
  type: string;
  createdAt: string;
}

interface BannerNotificationsResponse {
  status: number;
  data: {
    alerts: BannerNotification[];
  };
}

export type DashboardCardWithPair = Omit<DashboardCard, "type"> & {
  type: ApiCardType | "PCP_HEALTH_PLAN_PAIR";
};

type CardComponentProps = {
  shouldBlink?: boolean;
};

export const CARD_COMPONENTS: Record<ApiCardType, React.ComponentType<CardComponentProps>> = {
  DEMOGRAPHICS: DemographicsCard,
  PCP: PCPCard,
  HEALTH_PLAN: HealthPlanCard,
  CARE_MANAGEMENT: CareManagementCard,
  ADT_ALERTS: AdtAlertsCard,
  RISK_STRATIFICATION: RiskStratificationCard,
  CARE_GAPS: CareGaps,
  IMMUNIZATIONS: ImmunizationsCard,
  BEHAVIORAL_HEALTH_DIAGNOSES: BehavioralHealthCard,
  MEDICAL_DIAGNOSES: MedicalDiagnosesCard,
  RECENT_VISITS_ENCOUNTERS: RecentVisitsCard,
  TWELVE_MONTH_COST_UTILIZATION: CostUtilizationCard,
  MEDICATION_FILL_HISTORY: MedicationFillHistoryCard,
  SDOH_BARRIERS: SDOHBarriersCard,
  REFERRALS: ReferralsCard,
};
export const LAYOUT_GRID: Record<string, string> = {
  "100": "1fr",
  "50-50": "1fr 1fr",
  "3-col": "1fr 1fr 1fr",
  "70-30": "7fr 3fr",
  "30-70": "3fr 7fr",
  "grid": "1fr 1fr 1fr",
};

export const LAYOUT_COLUMN_COUNT: Record<string, number> = {
  "100": 1,
  "50-50": 2,
  "3-col": 3,
  "70-30": 2,
  "30-70": 2,
  "grid": 3,
};

// Banner notification type from API
interface BannerNotification {
  id: string;
  displayText: string;
  type: string;
  createdAt: string;
}

interface BannerNotificationsResponse {
  status: number;
  data: {
    alerts: BannerNotification[];
  };
}

const applyMdColspan = (colspan?: number): number => (colspan === 3 ? 2 : colspan || 1);

const buildCardsWithMdLayout = (normalizedCards: DashboardCardWithPair[]) => {
  const result = normalizedCards.map((card) => ({
    ...card,
    shouldSpanFullOnMd: false,
  }));

  let mdColumnPosition = 0;
  normalizedCards.forEach((card, index) => {
    const mdColspan = applyMdColspan(card.colspan);
    if (mdColspan === 2) {
      if (mdColumnPosition === 1 && index > 0) {
        result[index - 1].shouldSpanFullOnMd = true;
      }
      mdColumnPosition = 0;
      return;
    }
    mdColumnPosition = mdColumnPosition === 0 ? 1 : 0;
  });

  if (mdColumnPosition === 1 && result.length > 0) {
    result[result.length - 1].shouldSpanFullOnMd = true;
  }

  return result;
};

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedFromStore = useSelector(
    (s: RootState) => s.search.selectedPatient
  );
  const dashboardState = useSelector((s: RootState) => s.dashboard);
  const user = useSelector((s: RootState) => s.auth.user);
  const [blinkingCard, setBlinkingCard] = useState<
    ApiCardType | "PCP_HEALTH_PLAN_PAIR" | null
  >(null);
  const [bannerNotifications, setBannerNotifications] = useState<
    BannerNotification[]
  >([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const navigate = useNavigate();
  const { selectedItem } = useParams();
  // Track previous favorite states to detect changes
  const prevFavoriteStatesRef = useRef<Map<string, boolean>>(new Map());

  // Create refs map for all card types
  const cardRefsMap = useRef<
    Map<ApiCardType | "PCP_HEALTH_PLAN_PAIR", HTMLDivElement | null>
  >(new Map());

  // Mapping from banner alert types to card types
  const bannerTypeToCardType: Record<string, ApiCardType> = {
    ADT: "ADT_ALERTS",
    RISK: "RISK_STRATIFICATION",
  };

  const fetchBannerNotifications = async () => {
    // Use selectedItem instead of memberId from Redux
    if (!selectedItem) return;

    setIsLoadingNotifications(true);
    try {
      const response = await api.get<BannerNotificationsResponse>(
        routes.dashboard.getBannerNotifications(selectedItem)
      );

      if (response.data?.status === 200 && response.data?.data?.alerts) {
        console.log("notification");
        // Deduplicate by ID to prevent duplicate banners
        const alerts = response.data.data.alerts;
        const uniqueAlerts = alerts.filter(
          (alert, index, self) =>
            index === self.findIndex((a) => a.id === alert.id)
        );
        setBannerNotifications(uniqueAlerts);
      }
    } catch (error) {
      console.error("Error fetching banner notifications:", error);
      setBannerNotifications([]);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Mark notification as read
  const markNotificationAsRead = async (bannerId: string) => {
    try {
      await api.put(routes.dashboard.readBannerNotification(bannerId), {
        status: "read",
      });

      // Remove the notification from the list after successfully marking as read
      // Also deduplicate by ID to prevent any duplicates
      setBannerNotifications((prev) => {
        const filtered = prev.filter((notif) => notif.id !== bannerId);
        // Deduplicate remaining notifications by ID
        return filtered.filter(
          (notif, index, self) =>
            index === self.findIndex((n) => n.id === notif.id)
        );
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleScrollToCard = (bannerType: string, bannerId: string) => {
    // First, try to map banner type to card type
    let cardType: ApiCardType | undefined = bannerTypeToCardType[bannerType];

    if (!cardType) {
      // Check if the bannerType directly matches a card type
      if (ALL_CARD_TYPES.includes(bannerType as ApiCardType)) {
        cardType = bannerType as ApiCardType;
      }
    }

    if (!cardType) {
      console.warn(`No card type found for banner type: ${bannerType}`);
      return;
    }

    // Find the card in the dashboard state (check both static and dynamic cards)
    const card = dashboardState.cards.find((c) => c.type === cardType);
    if (!card) {
      console.warn(`Card not found in dashboard for type: ${cardType}`);
      return;
    }

    // Get the ref for this card type - use a small delay to ensure refs are set
    const scrollToCard = () => {
      const cardRef = cardRefsMap.current.get(cardType!);
      if (cardRef) {
        cardRef.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        setBlinkingCard(cardType!);

        setTimeout(() => {
          setBlinkingCard(null);
        }, 3000);

        // Mark notification as read after scrolling
        markNotificationAsRead(bannerId);
      } else {
        console.warn(`Ref not found for card type: ${cardType}. Retrying...`);
        // Retry after a short delay in case the ref hasn't been set yet
        setTimeout(() => {
          const retryRef = cardRefsMap.current.get(cardType!);
          if (retryRef) {
            retryRef.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            setBlinkingCard(cardType!);
            setTimeout(() => {
              setBlinkingCard(null);
            }, 3000);
            markNotificationAsRead(bannerId);
          } else {
            console.error(
              `Ref still not found for card type: ${cardType} after retry`
            );
          }
        }, 100);
      }
    };

    // Small delay to ensure DOM is updated and refs are set
    setTimeout(scrollToCard, 50);
  };

  const handleDismissNotification = (bannerId: string) => {
    markNotificationAsRead(bannerId);
  };

  // Always fetch dashboard cards on mount to ensure fresh data
  useEffect(() => {
    dispatch(fetchUserDashboardCards());
  }, [dispatch]);

  // Fetch banner notifications when selectedItem changes
  useEffect(() => {
    if (selectedItem) {
      fetchBannerNotifications();
    } else {
      setBannerNotifications([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem && selectedFromStore?.memberId !== selectedItem) {
      dispatch(
        setSelectedPatient({
          medicadeId: selectedFromStore?.medicadeId || "",
          memberId: selectedItem,
          memberFirstName: "",
          memberLastName: "",
          memberDob: "",
        } as Patient)
      );
    }
  }, [
    dispatch,
    selectedItem,
    selectedFromStore?.memberId,
    selectedFromStore?.medicadeId,
  ]);

  // Watch for favorite status changes and trigger blinking
  useEffect(() => {
    dashboardState.cards.forEach((card) => {
      const prevFavorite = prevFavoriteStatesRef.current.get(card.id);
      const currentFavorite = card.isFavorite;

      // If card just became favorited (changed from false/undefined to true)
      if (currentFavorite && prevFavorite === false) {
        // Trigger blink for this card
        setBlinkingCard(card.type);

        // Stop blinking after 3 seconds
        setTimeout(() => {
          setBlinkingCard(null);
        }, 3000);
      }

      // Update the previous state
      prevFavoriteStatesRef.current.set(card.id, currentFavorite);
    });
  }, [dashboardState.cards]);

  const showLanding = !selectedItem;

  // Normalize API cards: keep PCP stacked above Health Plan while preserving API order
  const normalizedCards = useMemo<DashboardCardWithPair[]>(() => {
    if (!dashboardState.cards.length) return [];

    const cards = [...dashboardState.cards];
    const pcpIndex = cards.findIndex((c) => c.type === "PCP");
    const healthPlanIndex = cards.findIndex((c) => c.type === "HEALTH_PLAN");

    // If both exist, replace them with a composite entry at the first occurrence
    if (pcpIndex !== -1 && healthPlanIndex !== -1) {
      const firstIndex = Math.min(pcpIndex, healthPlanIndex);
      const pcpCard = cards[pcpIndex];
      const healthPlanCard = cards[healthPlanIndex];
      const colspan =
        pcpCard.colspan ?? healthPlanCard.colspan ?? (pcpCard.colspan || 1);

      const filtered = cards.filter(
        (c) => c.type !== "PCP" && c.type !== "HEALTH_PLAN"
      ) as DashboardCardWithPair[];

      const pairCard: DashboardCardWithPair = {
        ...pcpCard,
        id: "pcp-health-plan-pair",
        type: "PCP_HEALTH_PLAN_PAIR" as DashboardCardWithPair["type"],
        colspan: colspan || 1,
      };

      filtered.splice(firstIndex, 0, pairCard);
      return filtered;
    }

    return cards as DashboardCardWithPair[];
  }, [dashboardState.cards]);

  const cardsWithMdLayout = useMemo(
    () => buildCardsWithMdLayout(normalizedCards),
    [normalizedCards]
  );

  // Banner notifications are always visible regardless of which cards are in the layout.
  // Deduplication is handled at render time.
  const visibleBannerNotifications = useMemo((): BannerNotification[] => {
    return bannerNotifications;
  }, [bannerNotifications]);

  // Only computed for the preferred layout. Returns the set of card types that are
  // actually rendered in the current preferred layout columns.
  // Returns null for the default layout — callers use null to mean "apply no filter"
  // so the default layout render path is completely unchanged.
  const renderedCardTypesInPreferredLayout = useMemo((): Set<string> | null => {
    const dashboardType = dashboardState.dashboard?.dashboardType || "default";
    if (dashboardType !== "preferred") return null;

    const layoutType = dashboardState.dashboard?.layoutType || "grid";
    const columnCount = LAYOUT_COLUMN_COUNT[layoutType] || 3;

    const renderedCardTypes = new Set<string>();
    cardsWithMdLayout
      .filter((card) => card.layoutPosition !== "fixed")
      .forEach((card) => {
        const colIndex = parseInt(card.layoutPosition || "0") - 1;
        if (colIndex >= 0 && colIndex < columnCount) {
          renderedCardTypes.add(card.type);
        }
      });

    return renderedCardTypes;
  }, [dashboardState.dashboard?.dashboardType, dashboardState.dashboard?.layoutType, cardsWithMdLayout]);

  const setCardRef = (
    cardType: ApiCardType | "PCP_HEALTH_PLAN_PAIR",
    element: HTMLDivElement | null
  ) => {
    if (cardType === "PCP_HEALTH_PLAN_PAIR") {
      if (element) {
        cardRefsMap.current.set("PCP", element);
        cardRefsMap.current.set("HEALTH_PLAN", element);
      } else {
        cardRefsMap.current.delete("PCP");
        cardRefsMap.current.delete("HEALTH_PLAN");
      }
      return;
    }

    if (element) {
      cardRefsMap.current.set(cardType, element);
    } else {
      cardRefsMap.current.delete(cardType);
    }
  };

  // Responsive col-span helper (md = 2 cols, lg = 3 cols)
  const getColSpanClasses = (
    colspan?: number,
    shouldSpanFullOnMd?: boolean,
    cardType?: ApiCardType | "PCP_HEALTH_PLAN_PAIR"
  ) => {
    // Special case: Make Behavioral Health full width on md screens
    if (cardType === "BEHAVIORAL_HEALTH_DIAGNOSES") {
      return "col-span-1 md:col-span-2 lg:col-span-1";
    }
    if (cardType === "MEDICAL_DIAGNOSES") {
      return "col-span-1 md:col-span-2 lg:col-span-1";
    }

    if (colspan === 2) return "col-span-1 md:col-span-2 lg:col-span-2";
    if (colspan === 3) return "col-span-1 md:col-span-2 lg:col-span-3";
    if (shouldSpanFullOnMd) return "col-span-1 md:col-span-2 lg:col-span-1";
    return "";
  };

  return (
    <AppLayout subHeaderContent={<DashboardHeader />}>
      <div
        className={
          showLanding
            ? "h-full flex flex-col justify-center items-center w-full max-w-5xl mx-auto"
            : "w-full"
        }>
        {showLanding ? (
          <>
            {/* Top section - Logo (Centered) */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "0px 40px 16px 40px",
                // marginTop: "129px",
              }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "16px",
                  width: "100%",
                  maxWidth: "1200px",
                }}>
                <div>
                  <img
                    src={LandingPageLightIcon}
                    alt='Dashboard'
                    className='block dark:hidden w-[280px] h-auto sm:w-[340px] md:w-[380px] lg:w-[457px]'
                  />

                  <img
                    src={landingPageDarkIcon}
                    alt='Dashboard'
                    className='hidden dark:block w-[280px] h-auto sm:w-[360px] md:w-[420px] lg:w-[457px]'
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                  }}>
                  <Typography
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    variant='semibold-22'
                    className='font-inter
                        font-semibold
                        text-center
                        leading-[30px]
                        text-[16px] sm:text-[18px] md:text-[20px] lg:text-[22px]
                        text-grey-800
                        dark:text-grey-100
                        mx-auto
                        max-w-2xl
                        px-4
                      '>
                    Hello {user?.first_name || "User"}! Search for a Patient
                  </Typography>

                  <Typography
                    variant='medium-16'
                    className='text-center text-sm sm:text-base md:text-lg text-grey-600 dark:text-grey-500 max-w-2xl mx-auto leading-relaxed'>
                    Select a Patient using the Search Bar to View
                    <span className='hidden md:inline lg:hidden'>
                      <br />
                    </span>
                    <span className='inline md:hidden lg:inline'>&nbsp;</span>
                    Information on the Dashboard.
                  </Typography>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className='p-1 space-y-5  pb-[20px]'>
            {/* Banner Alerts - Dynamic from API */}
            {/* Only show banners when selectedItem exists to prevent showing on empty dashboard */}
            {selectedItem && visibleBannerNotifications.length > 0 && (
              <div className='space-y-4'>
                {!isLoadingNotifications &&
                  // Deduplicate by ID before rendering to prevent duplicate banners
                  visibleBannerNotifications
                    ?.filter(
                      (notification, index, self) =>
                        index ===
                        self.findIndex((n) => n.id === notification.id)
                    )
                    ?.map((notification) => {
                      // Default layout: always show View Alert (original behaviour preserved).
                      // Preferred layout: only show View Alert when the associated card is in the layout.
                      const isPreferred = renderedCardTypesInPreferredLayout !== null;
                      const cardType = bannerTypeToCardType[notification.type];
                      const showViewAlert = !isPreferred || (!!cardType && renderedCardTypesInPreferredLayout.has(cardType));
                      return (
                      <BannerAlert
                        key={notification.id}
                        message={notification.displayText}
                        type={notification.type}
                        onScrollToCard={
                          showViewAlert
                              ? () => handleScrollToCard?.(notification?.type, notification?.id)
                              : undefined
                        }
                        onDismiss={() =>
                          handleDismissNotification?.(notification?.id)
                        }
                      />
                    );
                    })}
              </div>
            )}

            {/* DEPRICIATED: Legacy static rendering retained for reference
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] items-start'>
              <div ref={(el) => el ? cardRefsMap.current.set("DEMOGRAPHICS", el) : cardRefsMap.current.delete("DEMOGRAPHICS")}>
                <DemographicsCard shouldBlink={blinkingCard === "DEMOGRAPHICS"} />
              </div>
              <div className='gap-[16px] flex flex-col'>
                <div ref={(el) => el ? cardRefsMap.current.set("PCP", el) : cardRefsMap.current.delete("PCP")}>
                  <PCPCard shouldBlink={blinkingCard === "PCP"} />
                </div>
                <div ref={(el) => el ? cardRefsMap.current.set("HEALTH_PLAN", el) : cardRefsMap.current.delete("HEALTH_PLAN")}>
                  <HealthPlanCard shouldBlink={blinkingCard === "HEALTH_PLAN"} />
                </div>
              </div>
              <div ref={(el) => el ? cardRefsMap.current.set("CARE_MANAGEMENT", el) : cardRefsMap.current.delete("CARE_MANAGEMENT")}>
                <CareManagementCard shouldBlink={blinkingCard === "CARE_MANAGEMENT"} />
              </div>
            </div>

            {dynamicCards.length > 0 && (
              <>
                {(() => {
                  const rows: React.JSX.Element[] = [];
                  let currentRow: DashboardCard[] = [];
                  let currentRowColspan = 0;

                  const renderCurrentRow = () => {
                    if (currentRow.length === 0) return;

                    const hasColspan2 = currentRow.some((c) => c.colspan === 2);
                    const hasColspan1 = currentRow.some((c) => c.colspan === 1);

                    if (hasColspan2 && hasColspan1) {
                      rows.push(
                        <div
                          key={`row-${currentRow[0].id}`}
                          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] items-start grid-flow-row dense'>
                          {currentRow.map((c) => {
                            if (c.colspan === 2) {
                              return (
                                <div
                                  key={c.id}
                                  className={getColSpanClasses(c.colspan)}>
                                  {renderCard(c, true)}
                                </div>
                              );
                            } else {
                              return (
                                <div key={c.id} className={getColSpanClasses(c.colspan)}>
                                  {renderCard(c, true)}
                                </div>
                              );
                            }
                          })}
                        </div>
                      );
                    } else if (hasColspan2) {
                      rows.push(
                        <div
                          key={`row-${currentRow[0].id}`}
                          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] items-start grid-flow-row dense'>
                          {currentRow.map((c) => (
                            <div
                              key={c.id}
                              className={getColSpanClasses(c.colspan)}>
                              {renderCard(c, true)}
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      rows.push(
                        <div
                          key={`row-${currentRow[0].id}`}
                          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] items-start grid-flow-row dense'>
                          {currentRow.map((c) => (
                            <div key={c.id} className={getColSpanClasses(c.colspan)}>
                              {renderCard(c, true)}
                            </div>
                          ))}
                        </div>
                      );
                    }

                    currentRow = [];
                    currentRowColspan = 0;
                  };

                  dynamicCards.forEach((card) => {
                    if (card.colspan === 3) {
                      renderCurrentRow();
                      rows.push(
                        <div key={card.id} className={getColSpanClasses(card.colspan)}>
                          {renderCard(card, true)}
                        </div>
                      );
                    } else if (card.colspan === 2) {
                      if (currentRowColspan === 0) {
                        currentRow.push(card);
                        currentRowColspan = 2;
                      } else if (currentRowColspan === 1) {
                        currentRow.push(card);
                        currentRowColspan = 3;
                        renderCurrentRow();
                      } else {
                        renderCurrentRow();
                        currentRow.push(card);
                        currentRowColspan = 2;
                      }
                    } else {
                      if (currentRowColspan === 0) {
                        currentRow.push(card);
                        currentRowColspan = 1;
                      } else if (currentRowColspan === 1) {
                        const hasColspan2 = currentRow.some(
                          (c) => c.colspan === 2
                        );
                        if (hasColspan2) {
                          renderCurrentRow();
                          currentRow.push(card);
                          currentRowColspan = 1;
                        } else {
                          currentRow.push(card);
                          currentRowColspan = 2;
                        }
                      } else if (currentRowColspan === 2) {
                        const hasColspan2 = currentRow.some(
                          (c) => c.colspan === 2
                        );
                        if (hasColspan2) {
                          currentRow.push(card);
                          currentRowColspan = 3;
                          renderCurrentRow();
                        } else {
                          currentRow.push(card);
                          currentRowColspan = 3;
                          renderCurrentRow();
                        }
                      } else {
                        renderCurrentRow();
                        currentRow.push(card);
                        currentRowColspan = 1;
                      }
                    }
                  });

                  renderCurrentRow();

                  return rows;
                })()}
              </>
            )}
            */}

            {(() => {
              const dashboardMetadata = dashboardState.dashboard;
              const activeLayout = dashboardMetadata?.dashboardType || "default";
              const layoutType = dashboardMetadata?.layoutType || "grid";

              if (activeLayout === "preferred") {
                return (
                  <PreferredLayout
                    cardsWithMdLayout={cardsWithMdLayout}
                    layoutType={layoutType}
                    blinkingCard={blinkingCard}
                    setCardRef={setCardRef}
                    cardComponents={CARD_COMPONENTS}
                    layoutGrid={LAYOUT_GRID}
                    layoutColumnCount={LAYOUT_COLUMN_COUNT}
                  />
                );
              }

              if (cardsWithMdLayout.length > 0) {
                // Default Layout Logic (unchanged)
                const columnCount = LAYOUT_COLUMN_COUNT[layoutType] || 3;
                const gridTemplate = LAYOUT_GRID[layoutType] || LAYOUT_GRID["grid"];

                if (layoutType !== "grid") {
                  const columns: DashboardCardWithPair[][] = Array.from(
                    { length: columnCount },
                    () => []
                  );

                  cardsWithMdLayout
                    .filter(card => card.layoutPosition !== "fixed")
                    .forEach((card) => {
                      // Map layoutPosition "1", "2", "3" to index 0, 1, 2
                      const colIndex = parseInt(card.layoutPosition || "1") - 1;
                      if (colIndex >= 0 && colIndex < columnCount) {
                        columns[colIndex].push(card);
                      } else {
                        // Fallback to first column if position is invalid
                        columns[0].push(card);
                      }
                    });

                  return (
                    <div
                      className="grid gap-[20px] items-start"
                      style={{ gridTemplateColumns: gridTemplate }}
                    >
                      {columns.map((colCards, index) => {
                        const fixedCardsSorted = dashboardState.cards
                          .filter((c) => c.layoutPosition === "fixed")
                          .sort((a, b) => a.order - b.order);
                        const fixedCardOne = fixedCardsSorted[0];
                        const fixedCardTwo = fixedCardsSorted[1];

                        return (
                          <div key={`col-${index}`} className="flex flex-col gap-[20px] min-w-0">
                            {/* Fixed cards driven by layoutPosition === "fixed" from API */}
                            {index === 0 && fixedCardOne && (() => {
                              const FixedCard = CARD_COMPONENTS[fixedCardOne.type];
                              return FixedCard ? (
                                <div ref={(el) => setCardRef(fixedCardOne.type, el)}>
                                  <FixedCard shouldBlink={blinkingCard === fixedCardOne.type} />
                                </div>
                              ) : null;
                            })()}
                            {((columnCount === 1 && index === 0) || (columnCount > 1 && index === 1)) && fixedCardTwo && (() => {
                              const FixedCard = CARD_COMPONENTS[fixedCardTwo.type];
                              return FixedCard ? (
                                <div ref={(el) => setCardRef(fixedCardTwo.type, el)}>
                                  <FixedCard shouldBlink={blinkingCard === fixedCardTwo.type} />
                                </div>
                              ) : null;
                            })()}

                            {colCards.map((card) => {
                              const CardComponent = card.type === "PCP_HEALTH_PLAN_PAIR"
                                ? null
                                : CARD_COMPONENTS[card.type as ApiCardType];

                              const shouldBlink = card.type === "PCP_HEALTH_PLAN_PAIR"
                                ? blinkingCard === "PCP" || blinkingCard === "HEALTH_PLAN"
                                : blinkingCard === card.type;

                              return (
                                <div key={card.id} ref={(el) => setCardRef(card.type, el)}>
                                  {card.type === "PCP_HEALTH_PLAN_PAIR" ? (
                                    <div className="flex flex-col gap-[16px]">
                                      <div ref={(el) => setCardRef("PCP", el)}>
                                        <PCPCard shouldBlink={blinkingCard === "PCP"} />
                                      </div>
                                      <div ref={(el) => setCardRef("HEALTH_PLAN", el)}>
                                        <HealthPlanCard shouldBlink={blinkingCard === "HEALTH_PLAN"} />
                                      </div>
                                    </div>
                                  ) : (
                                    CardComponent && <CardComponent shouldBlink={shouldBlink} />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Legacy "grid" layout behavior
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[20px] items-start">
                    {cardsWithMdLayout.map((card) => {
                      const colspanClass = getColSpanClasses(
                        card.colspan,
                        card.shouldSpanFullOnMd,
                        card.type
                      );

                      const shouldBlink =
                        card.type === "PCP_HEALTH_PLAN_PAIR"
                          ? blinkingCard === "PCP" || blinkingCard === "HEALTH_PLAN"
                          : blinkingCard === card.type;

                      return (
                        <div
                          key={card.id}
                          className={colspanClass}
                          ref={(el) => setCardRef(card.type, el)}>
                          {card.type === "PCP_HEALTH_PLAN_PAIR" ? (
                            <div className="flex flex-col gap-[16px]">
                              <PCPCard shouldBlink={blinkingCard === "PCP"} />
                              <HealthPlanCard
                                shouldBlink={blinkingCard === "HEALTH_PLAN"}
                              />
                            </div>
                          ) : (
                            (() => {
                              const CardComponent =
                                CARD_COMPONENTS[card.type as ApiCardType];
                              return (
                                CardComponent && (
                                  <CardComponent shouldBlink={shouldBlink} />
                                )
                              );
                            })()
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Dashboard;
